import { useCallback, useMemo, useState } from 'react'

import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PhoneIcon from '@mui/icons-material/Phone'
import SyncIcon from '@mui/icons-material/Sync'
import TuneIcon from '@mui/icons-material/Tune'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Popover,
  Select,
  Slider,
  Switch,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  GoogleMap,
  Marker,
  OverlayView,
  useJsApiLoader,
  type Libraries,
} from '@react-google-maps/api'
import { differenceInMonths, format } from 'date-fns'

import { DATE_TIME_FORMAT } from 'constants/DateFormats'
import {
  SUPPLY_DEMAND_MAP_MONTHS_OPTIONS,
  SupplyDemandMapMonths,
} from 'constants/EntityConstants/Reports'
import { API_KEY, BRISTOL_COORDINATES } from 'constants/TrackingMap'

import { DriverMarkerCluster, type DriverPopupData } from './DriverMarkerCluster'
import { MapHeatmapLayer } from './MapHeatmapLayer'
import { DRIVER_STATUS_OPTIONS, DriverStatusKey, useSupplyDemandMap } from './useSupplyDemandMap'

const GOOGLE_MAPS_LIBRARIES: Libraries = []

type LayerKey = 'driverHomeLocations' | 'collectionAddresses' | 'deliveryAddresses'

interface LayerState {
  visible: boolean
  heatmap: boolean
}

type LayersState = Record<LayerKey, LayerState>

type MarkerType = 'driver' | 'collection' | 'delivery'

interface SelectedMarker {
  type: MarkerType
  position: { lat: number; lng: number }
  drivers?: DriverPopupData[]
  driverIndex?: number
}

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' }

const MAP_OPTIONS: google.maps.MapOptions = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  zoomControl: true,
}

export const SupplyDemandDistribution = () => {
  const theme = useTheme()

  const { isLoaded: isMapApiLoaded } = useJsApiLoader({
    id: 'supply-demand-map',
    googleMapsApiKey: API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  const {
    months,
    setMonths,
    hasLoaded,
    lastUpdated,
    loading: isFetching,
    progress,
    driverStatuses,
    toggleStatus,
    collectionPoints,
    deliveryPoints,
    filteredDriverLocations,
    handleLoad,
  } = useSupplyDemandMap()

  const loadPercent =
    progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0

  const [layers, setLayers] = useState<LayersState>({
    driverHomeLocations: { visible: true, heatmap: true },
    collectionAddresses: { visible: true, heatmap: true },
    deliveryAddresses: { visible: true, heatmap: true },
  })
  const [selectedMarker, setSelectedMarker] = useState<SelectedMarker | null>(null)
  const [heatmapSettings, setHeatmapSettings] = useState({
    radius: 40,
    intensity: 2,
    threshold: 0.05,
  })
  const [heatmapSettingsAnchor, setHeatmapSettingsAnchor] = useState<HTMLButtonElement | null>(null)

  const toggleLayerVisible = (key: LayerKey) =>
    setLayers((prev) => ({ ...prev, [key]: { ...prev[key], visible: !prev[key].visible } }))

  const toggleLayerHeatmap = (key: LayerKey) =>
    setLayers((prev) => ({ ...prev, [key]: { ...prev[key], heatmap: !prev[key].heatmap } }))

  const driverColor = theme.palette.primary.main
  const collectionColor = theme.palette.info.main
  const deliveryColor = theme.palette.warning.main

  const statusColors: Record<DriverStatusKey, string> = {
    currentActive: theme.palette.success.dark,
    currentInactive: theme.palette.warning.main,
    currentLapsed: theme.palette.error.main,
    currentDormant: theme.palette.text.disabled,
  }

  const makeCircleIcon = (color: string): google.maps.Symbol => ({
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 0.9,
    strokeColor: '#fff',
    strokeWeight: 1.5,
    scale: 5,
  })

  const collectionIcon = useMemo(
    () => (isMapApiLoaded ? makeCircleIcon(collectionColor) : undefined),
    [isMapApiLoaded, collectionColor],
  )
  const deliveryIcon = useMemo(
    () => (isMapApiLoaded ? makeCircleIcon(deliveryColor) : undefined),
    [isMapApiLoaded, deliveryColor],
  )

  const driverHeatmapPoints = useMemo(
    () => filteredDriverLocations.map((p) => ({ lat: p.lat, lng: p.lng })),
    [filteredDriverLocations],
  )

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      const all = [
        ...filteredDriverLocations.map((p) => ({ lat: p.lat, lng: p.lng })),
        ...collectionPoints,
        ...deliveryPoints,
      ]
      if (!all.length) return
      const bounds = new google.maps.LatLngBounds()
      all.forEach((p) => bounds.extend(p))
      map.fitBounds(bounds, 40)
    },
    [filteredDriverLocations, collectionPoints, deliveryPoints],
  )

  const getDriverStatus = (lastActivityAt: string | null | undefined) => {
    const ago = lastActivityAt ? differenceInMonths(new Date(), new Date(lastActivityAt)) : Infinity
    const option =
      DRIVER_STATUS_OPTIONS.find((o) => ago >= o.min && (o.max === null || ago < o.max)) ??
      DRIVER_STATUS_OPTIONS[DRIVER_STATUS_OPTIONS.length - 1]
    return { label: option.label, color: statusColors[option.key] }
  }

  const LAYER_CONFIG: { key: LayerKey; label: string; color: string }[] = [
    { key: 'driverHomeLocations', label: 'Driver Home Locations', color: driverColor },
    { key: 'collectionAddresses', label: 'Collection Addresses', color: collectionColor },
    { key: 'deliveryAddresses', label: 'Delivery Addresses', color: deliveryColor },
  ]

  const MARKER_INFO: Record<MarkerType, { label: string; color: string }> = {
    driver: { label: 'Driver Home Location', color: driverColor },
    collection: { label: 'Collection Address', color: collectionColor },
    delivery: { label: 'Delivery Address', color: deliveryColor },
  }

  const isLoading = isFetching || !isMapApiLoaded

  const driverCount = selectedMarker?.drivers?.length ?? 0
  const currentDriverIndex = selectedMarker?.driverIndex ?? 0
  const currentDriver = selectedMarker?.drivers?.[currentDriverIndex]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 3, gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant='h5' color='primary'>
            Supply-Demand Distribution Map
          </Typography>
          <Typography variant='body2' color='text.secondary' mt={0.5}>
            Visualise the geographic distribution of driver home locations, collection addresses,
            and delivery addresses.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, ml: 3 }}>
          {lastUpdated && (
            <Typography variant='body2' color='text.secondary' noWrap>
              Last updated: {format(lastUpdated, DATE_TIME_FORMAT)}
            </Typography>
          )}
          <Button
            variant='outlined'
            startIcon={isLoading && hasLoaded ? <CircularProgress size={14} /> : <SyncIcon />}
            onClick={handleLoad}
            disabled={isLoading && hasLoaded}
            sx={{ whiteSpace: 'nowrap' }}>
            {hasLoaded ? 'REFRESH NOW' : 'LOAD MAP'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant='caption' color='text.secondary' display='block' mb={0.75}>
            Data period (applies to collection
            <br />
            and delivery addresses)
          </Typography>
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <Select
              value={months}
              onChange={(e) => setMonths(Number(e.target.value) as SupplyDemandMapMonths)}
              startAdornment={
                <InputAdornment position='start'>
                  <CalendarTodayIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              }>
              {SUPPLY_DEMAND_MAP_MONTHS_OPTIONS.map((m) => (
                <MenuItem key={m} value={m}>
                  Last {m} months
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant='body2' color='text.secondary' mb={0.75}>
            Driver statuses to show based on last activity
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {DRIVER_STATUS_OPTIONS.map(({ key, label, sub }) => (
              <Box
                key={key}
                sx={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}
                onClick={() => toggleStatus(key)}>
                <Checkbox
                  checked={driverStatuses[key]}
                  size='small'
                  sx={{ pt: 0, pr: 0.5 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleStatus(key)}
                />
                <Box>
                  <Typography variant='body2'>{label}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {sub}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexGrow: 1, gap: 2, minHeight: 500 }}>
        <Box sx={{ width: 260, flexShrink: 0 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
              Map Layers
            </Typography>
            <IconButton
              size='small'
              onClick={(e) => setHeatmapSettingsAnchor(e.currentTarget)}
              title='Heatmap settings'>
              <TuneIcon fontSize='small' />
            </IconButton>
          </Box>

          <Popover
            open={Boolean(heatmapSettingsAnchor)}
            anchorEl={heatmapSettingsAnchor}
            onClose={() => setHeatmapSettingsAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
            <Box sx={{ p: 2, width: 220 }}>
              <Typography variant='subtitle2' mb={1.5}>
                Heatmap Settings
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Radius
              </Typography>
              <Slider
                size='small'
                value={heatmapSettings.radius}
                min={10}
                max={120}
                step={5}
                valueLabelDisplay='auto'
                onChange={(_, v) => setHeatmapSettings((s) => ({ ...s, radius: v as number }))}
              />
              <Typography variant='caption' color='text.secondary'>
                Intensity
              </Typography>
              <Slider
                size='small'
                value={heatmapSettings.intensity}
                min={0.5}
                max={8}
                step={0.5}
                valueLabelDisplay='auto'
                onChange={(_, v) => setHeatmapSettings((s) => ({ ...s, intensity: v as number }))}
              />
              <Typography variant='caption' color='text.secondary'>
                Threshold
              </Typography>
              <Slider
                size='small'
                value={heatmapSettings.threshold}
                min={0.01}
                max={0.3}
                step={0.01}
                valueLabelDisplay='auto'
                onChange={(_, v) => setHeatmapSettings((s) => ({ ...s, threshold: v as number }))}
              />
            </Box>
          </Popover>

          {LAYER_CONFIG.map(({ key, label, color }) => (
            <Box key={key} mb={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant='body2'>{label}</Typography>
                </Box>
                <Switch
                  checked={layers[key].visible}
                  onChange={() => toggleLayerVisible(key)}
                  size='small'
                  sx={{ '& .MuiSwitch-thumb': { backgroundColor: color } }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pl: 2.5,
                }}>
                <Typography variant='body2' color='text.secondary'>
                  Heatmap
                </Typography>
                <Switch
                  checked={layers[key].heatmap}
                  onChange={() => toggleLayerHeatmap(key)}
                  size='small'
                  sx={{ '& .MuiSwitch-thumb': { backgroundColor: color } }}
                />
              </Box>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />

          <Typography variant='subtitle2' color='primary' fontWeight='bold' mb={1}>
            Legend
          </Typography>
          {LAYER_CONFIG.map(({ label, color }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: color,
                  flexShrink: 0,
                }}
              />
              <Typography variant='body2'>{label}</Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            position: 'relative',
            borderRadius: 1,
            overflow: 'hidden',
          }}>
          {!hasLoaded ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'background.paper',
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: 1,
                gap: 2,
              }}>
              <Typography variant='body1' color='text.secondary'>
                Press <strong>Load Map</strong> to fetch distribution data
              </Typography>
              <Button variant='contained' startIcon={<SyncIcon />} onClick={handleLoad}>
                LOAD MAP
              </Button>
            </Box>
          ) : (
            <>
              {isFetching && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 2,
                  }}>
                  <LinearProgress
                    variant='determinate'
                    value={loadPercent}
                    sx={{ height: 4, borderRadius: 0 }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      px: 1,
                      pt: 0.5,
                    }}>
                    <Typography
                      variant='caption'
                      sx={{
                        backgroundColor: 'background.paper',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        opacity: 0.9,
                        lineHeight: 1.4,
                      }}>
                      Loading {loadPercent}%…
                    </Typography>
                  </Box>
                </Box>
              )}
              {isMapApiLoaded && (
                <GoogleMap
                  mapContainerStyle={MAP_CONTAINER_STYLE}
                  center={BRISTOL_COORDINATES}
                  zoom={6}
                  options={MAP_OPTIONS}
                  onLoad={onMapLoad}
                  onClick={() => setSelectedMarker(null)}
                  onZoomChanged={() => setSelectedMarker(null)}>
                  {layers.deliveryAddresses.visible &&
                    deliveryPoints.map((p, i) => (
                      <Marker
                        key={`del-${i}`}
                        position={p}
                        icon={deliveryIcon}
                        zIndex={1}
                        onClick={() => setSelectedMarker({ type: 'delivery', position: p })}
                      />
                    ))}
                  {layers.collectionAddresses.visible &&
                    collectionPoints.map((p, i) => (
                      <Marker
                        key={`col-${i}`}
                        position={p}
                        icon={collectionIcon}
                        zIndex={2}
                        onClick={() => setSelectedMarker({ type: 'collection', position: p })}
                      />
                    ))}
                  <DriverMarkerCluster
                    drivers={filteredDriverLocations}
                    visible={layers.driverHomeLocations.visible}
                    color={driverColor}
                    getDriverStatus={getDriverStatus}
                    onMarkerClick={(position, drivers) =>
                      setSelectedMarker({ type: 'driver', position, drivers, driverIndex: 0 })
                    }
                  />
                  {layers.deliveryAddresses.visible &&
                    layers.deliveryAddresses.heatmap &&
                    deliveryPoints.length > 0 && (
                      <MapHeatmapLayer
                        id='heatmap-delivery'
                        points={deliveryPoints}
                        radius={heatmapSettings.radius}
                        intensity={heatmapSettings.intensity}
                        threshold={heatmapSettings.threshold}
                      />
                    )}
                  {layers.collectionAddresses.visible &&
                    layers.collectionAddresses.heatmap &&
                    collectionPoints.length > 0 && (
                      <MapHeatmapLayer
                        id='heatmap-collection'
                        points={collectionPoints}
                        radius={heatmapSettings.radius}
                        intensity={heatmapSettings.intensity}
                        threshold={heatmapSettings.threshold}
                      />
                    )}
                  {layers.driverHomeLocations.visible &&
                    layers.driverHomeLocations.heatmap &&
                    filteredDriverLocations.length > 0 && (
                      <MapHeatmapLayer
                        id='heatmap-driver'
                        points={driverHeatmapPoints}
                        radius={heatmapSettings.radius}
                        intensity={heatmapSettings.intensity}
                        threshold={heatmapSettings.threshold}
                      />
                    )}
                  {selectedMarker && (
                    <OverlayView
                      position={selectedMarker.position}
                      mapPaneName={OverlayView.FLOAT_PANE}>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          transform: 'translate(-50%, calc(-100% - 12px))',
                          background: theme.palette.background.paper,
                          borderRadius: 8,
                          boxShadow: '0 2px 12px rgba(0,0,0,0.22)',
                          padding: '12px 14px',
                          maxWidth: 240,
                          minWidth: 180,
                          position: 'relative',
                          fontFamily: 'sans-serif',
                        }}>
                        <button
                          onClick={() => setSelectedMarker(null)}
                          style={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 2,
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            lineHeight: 1,
                          }}>
                          <CloseIcon style={{ fontSize: 16 }} />
                        </button>

                        {selectedMarker.type === 'driver' && currentDriver ? (
                          <>
                            {driverCount > 1 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.5,
                                  mb: 1,
                                }}>
                                <IconButton
                                  size='small'
                                  disabled={currentDriverIndex === 0}
                                  onClick={() =>
                                    setSelectedMarker((prev) =>
                                      prev
                                        ? { ...prev, driverIndex: currentDriverIndex - 1 }
                                        : prev,
                                    )
                                  }>
                                  <ChevronLeftIcon fontSize='small' />
                                </IconButton>
                                <Typography variant='caption' color='text.secondary' noWrap>
                                  Driver {currentDriverIndex + 1} of {driverCount}
                                </Typography>
                                <IconButton
                                  size='small'
                                  disabled={currentDriverIndex === driverCount - 1}
                                  onClick={() =>
                                    setSelectedMarker((prev) =>
                                      prev
                                        ? { ...prev, driverIndex: currentDriverIndex + 1 }
                                        : prev,
                                    )
                                  }>
                                  <ChevronRightIcon fontSize='small' />
                                </IconButton>
                              </Box>
                            )}
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: 15,
                                color: driverColor,
                                marginBottom: 2,
                                paddingRight: 20,
                              }}>
                              {currentDriver.fullName}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                                marginBottom: 10,
                              }}>
                              {currentDriver.companyName}
                            </div>
                            {currentDriver.vehicleTypes.length > 0 && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 6,
                                  fontSize: 12,
                                  color: theme.palette.text.primary,
                                  marginBottom: 6,
                                }}>
                                <LocalShippingIcon
                                  style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}
                                />
                                <span style={{ wordBreak: 'break-word' }}>
                                  Fleet: {currentDriver.vehicleTypes.join(', ')}
                                </span>
                              </div>
                            )}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                fontSize: 12,
                                color: theme.palette.text.primary,
                                marginBottom: 10,
                              }}>
                              <PhoneIcon style={{ fontSize: 15, flexShrink: 0 }} />
                              <span>{currentDriver.telephoneNumber}</span>
                            </div>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                backgroundColor: currentDriver.statusColor + '22',
                                borderRadius: 12,
                                padding: '3px 10px',
                                fontSize: 12,
                                fontWeight: 600,
                                color: currentDriver.statusColor,
                              }}>
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: currentDriver.statusColor,
                                  display: 'inline-block',
                                  flexShrink: 0,
                                }}
                              />
                              {currentDriver.statusLabel}
                            </span>
                          </>
                        ) : (
                          <>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: MARKER_INFO[selectedMarker.type].color,
                                marginBottom: 4,
                                paddingRight: 20,
                              }}>
                              {MARKER_INFO[selectedMarker.type].label}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: theme.palette.text.disabled,
                              }}>
                              {selectedMarker.position.lat.toFixed(4)},{' '}
                              {selectedMarker.position.lng.toFixed(4)}
                            </div>
                          </>
                        )}

                        <div
                          style={{
                            position: 'absolute',
                            bottom: -8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderTop: `8px solid ${theme.palette.background.paper}`,
                            filter: 'drop-shadow(0 2px 1px rgba(0,0,0,0.12))',
                          }}
                        />
                      </div>
                    </OverlayView>
                  )}
                </GoogleMap>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}
